import { x } from '@xstyled/styled-components';
import React, { FC, useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { useAcknowledgePolicyMutation } from '~/data';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons';
import { Checkbox } from '~/ui/checkbox/Checkbox';
import { TrainingState, useTrainingStore } from '../state';
import styled from 'styled-components';
import ExtImg from '~/assets/icons/external-link.svg';
import blueJones from '~/assets/fonts/blue-jones.woff2';
import SignatureCanvas from 'react-signature-canvas';
import { TickMarkCapsule } from '~/ui/capsules';
import WebFont from 'webfontloader';
import { Overlay } from '~/ui/overlay/Overlay';
import { Modal } from '~/ui/modal/Modal';
import { Tooltip } from '~/ui/tooltip/Tooltip';

const ExtLinkImg = styled(ExtImg)`
  width: 16px;
  margin-left: 7px;
  margin-top: 7px;
`;

const SignatureBlock = styled.div`
  .signDesc {
    padding-bottom: 10px;
  }
  h3 {
    text-align: center;
  }
  .borderForSignature {
    border: 1px solid #e0e0e0;
    margin-top: 5px;
    margin-bottom: 5px;
    border-radius: 12px;
  }
  .btnSection {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .tabBtn {
    width: 49%;
    justify-content: center;
    display: flex;
    border: 0px solid;
    padding: 9px;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    border-bottom: 3px solid #e0e0e0;
    font-weight: 900;
    font-family: sans-serif;
    font-size: 14px;
    background-color: #fff;
    color: #2e2e2e;
  }
  .tabBtn.active {
    background-color: #f8f1ff;
    color: #9738ff;
    border-bottom: 3px solid #9738ff;
  }
  .inpTypeSignature {
    width: 100%;
    margin-top: 5px;
    margin-bottom: 5px;
    border-radius: 10px;
    padding: 10px;
    padding-left: 16px;
    font-size: 14px;
    border: 0px solid;
    background-color: #f5f5f5;
  }
  button {
    svg {
      background: #fff;
      border-radius: 10px;
    }
  }
`;

const PolicyBlockForLinks = styled.ul`
  margin: 10px 0px 10px 10%;
  li {
    display: flex;
  }
  p {
    cursor: pointer;
    color: #9738ff !important;
    text-align: left;
    padding-left: 30px;
    padding-bottom: 3px;
    padding-top: 3px;
    span {
      background-color: #9738ff;
      margin-left: -30px;
      display: inline-flex !important;
    }
  }
`;

const TitleForPopup = styled.div`
  padding: 10px 20px 10px;
  display: flex;
  justify-content: space-between;
  span {
    padding-top: 5px;
  }
`;

const PolicyLink: FC<{
  url: string;
  name?: string;
  onClick?: (event: React.UIEvent<HTMLElement>) => void;
  textDecoration?: string;
}> = ({ url, name = t`policy`, onClick, textDecoration = 'underline' }) => {
  const handleContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  return (
    <li
      tabIndex={0}
      role="button"
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick!(e);
        }
      }}
    >
      <x.p
        color="accentDark"
        fontWeight="bold"
        textDecoration={textDecoration}
        p="2px"
        display="block"
        onContextMenu={handleContextMenu}
      >
        {!textDecoration ? <TickMarkCapsule /> : false} {name}
      </x.p>
      <ExtLinkImg />
    </li>
  );
};

const selector = (s: TrainingState) =>
  select(
    s,
    'title',
    'learnerTrainingId',
    'policyAcknowledgementSignature',
    'policiesForSignature'
  );

export const AcceptPolicy: FC<{
  accept: () => void;
}> = ({ accept }) => {
  const {
    title,
    learnerTrainingId,
    policyAcknowledgementSignature,
    policiesForSignature
  } = useTrainingStore(selector);
  const [read, setRead] = useState(false);
  const [acknowledge] = useAcknowledgePolicyMutation();
  const [openPolicies, setOpenPolicies] = useState<Array<number>>([]);
  const [tab, setTab] = useState<string>('draw');
  const [txtSignature, SetTxtSignature] = useState<string>('');
  const [disabledSaveBtn, SetDisabledSaveBtn] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [urlForIframe, setUrlForIframe] = useState<string>('');
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const sigCanvas = useRef<SignatureCanvas>({} as unknown as SignatureCanvas);

  const GetImg = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const dataForImg = sigCanvas.current?.toDataURL() as string;

    if (policiesForSignature) {
      const policyIds = policiesForSignature
        .map(policy => policy?.id)
        .filter(Boolean);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      acknowledge({
        variables: {
          policyIds: policyIds,
          learnerTrainingUuid: learnerTrainingId,
          signature: dataForImg
        }
      });
    }
    accept();
  };

  const AddText = (text: string) => {
    WebFont.load({
      custom: {
        families: ['Bluejones'],
        urls: [blueJones]
      },
      active: () => {
        const cx = document?.querySelector('canvas')?.getContext('2d');

        if (cx) {
          cx.font = '28px Bluejones';
          cx.fillStyle = '#000000'; // Use fillStyle for text color
          cx.fillText(text, 10, 50);
        }
      }
    });
  };

  const clearCanvas = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    sigCanvas?.current?.clear();
    SetDisabledSaveBtn(true);
  };

  useEffect(() => {
    if (
      openPolicies &&
      policiesForSignature &&
      policiesForSignature.length > 0
    ) {
      const hasViewedAllPolicies =
        openPolicies.length === policiesForSignature.length;
      const hasSignature =
        tab === 'draw'
          ? !sigCanvas?.current?.isEmpty()
          : txtSignature.trim().length > 0;

      SetDisabledSaveBtn(!(hasViewedAllPolicies && hasSignature));
    }
  }, [openPolicies, tab, txtSignature]);

  const eventHendlerForTooltip = (value: boolean) => {
    setShowTooltip(value);
  };

  return (
    <>
      <div className="instructions">
        <b
          style={{ fontSize: '18px' }}
          id="description-policy"
        >{t`Please review the policy and return to this window to complete your training.`}</b>
        <PolicyBlockForLinks aria-describedby="description-policy">
          {(policiesForSignature || []).map((policy, i) => (
            <PolicyLink
              key={'link_' + policy?.policyUrl!}
              url={policy?.policyUrl!}
              name={policy?.name}
              textDecoration={!openPolicies.includes(i) ? 'underline' : ''}
              onClick={() => {
                if (policy?.policyUrl) {
                  setUrlForIframe(policy?.policyUrl);
                  setOpen(true);
                }

                if (!openPolicies.includes(i)) {
                  setOpenPolicies(prev => (prev ? [...prev, i] : [i]));
                }
              }}
            />
          ))}
        </PolicyBlockForLinks>
      </div>
      <x.div display="flex" flexDirection="column" alignItems="center" gap="sm">
        {policyAcknowledgementSignature ? (
          policiesForSignature?.length ? (
            <SignatureBlock>
              <div
                tabIndex={0}
                className="signDesc"
              >{t`I confirm that I have reviewed, understood, and accept the content of the documents above.`}</div>
              <x.h3>{t`Add your signature`}</x.h3>
              <div className="btnSection">
                <button
                  aria-label={t`Draw Signature`}
                  className={`tabBtn ${tab == 'draw' ? 'active' : ''}`}
                  onClick={() => {
                    setTab('draw');
                    SetTxtSignature('');
                    clearCanvas();
                  }}
                >
                  {t`Sign by hand`}
                </button>
                <button
                  aria-label={t`Type Signature`}
                  className={`tabBtn ${tab == 'type' ? 'active' : ''}`}
                  onClick={() => {
                    setTab('type');
                    SetTxtSignature('');
                    clearCanvas();

                    setTimeout(() => {
                      if (window.document) {
                        const element = window?.document?.querySelector(
                          '.inpTypeSignature'
                        ) as HTMLElement;

                        if (element) {
                          element.focus();
                        }
                      }
                    }, 100);
                  }}
                >
                  {t`Sign with Keyboard`}
                </button>
              </div>
              {tab == 'type' ? (
                <>
                  <p
                    style={{ textAlign: 'left', fontSize: '14px' }}
                  >{t`Your full name`}</p>
                  <input
                    aria-label={t`Your full name`}
                    className="inpTypeSignature"
                    type="text"
                    name="txtSignature"
                    value={txtSignature}
                    placeholder={t`Type here`}
                    onChange={e => {
                      clearCanvas();
                      SetTxtSignature(e.target.value);
                      AddText(e.target.value);
                      const hasViewedAllPolicies =
                        openPolicies.length === policiesForSignature.length;
                      SetDisabledSaveBtn(
                        !hasViewedAllPolicies && e.target.value ? false : true
                      );
                    }}
                  />
                </>
              ) : (
                false
              )}
              <div className="borderForSignature">
                <SignatureCanvas
                  penColor="#000"
                  backgroundColor="#FFF"
                  ref={sigCanvas}
                  canvasProps={{
                    width: 352,
                    height: 100,
                    className: 'sigCanvas'
                  }}
                  onEnd={() => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    if (!sigCanvas?.current?.isEmpty()) {
                      const hasViewedAllPolicies =
                        openPolicies.length === policiesForSignature.length;
                      SetDisabledSaveBtn(!hasViewedAllPolicies);
                    }
                  }}
                />
              </div>
              <div className="btnSection">
                <Button
                  style={{ backgroundColor: 'grey' }}
                  onClick={() => {
                    clearCanvas();
                    SetTxtSignature('');
                  }}
                >{t`Clear`}</Button>
                <Button
                  style={
                    disabledSaveBtn
                      ? { backgroundColor: 'grey', color: '#FFF' }
                      : { backgroundColor: '#9738FF', color: '#FFF' }
                  }
                  onClick={GetImg}
                  disabled={disabledSaveBtn}
                  onMouseOver={
                    disabledSaveBtn
                      ? () => eventHendlerForTooltip(true)
                      : undefined
                  }
                  onMouseLeave={
                    disabledSaveBtn
                      ? () => eventHendlerForTooltip(false)
                      : undefined
                  }
                >{t`Next`}</Button>
                {showTooltip && (
                  <Tooltip
                    placement="bottom-right"
                    bottom="84px"
                    right="36px"
                    style={{ width: '320px' }}
                  >
                    {t`Please open and read the documents before continuing.`}
                  </Tooltip>
                )}
              </div>
            </SignatureBlock>
          ) : (
            false
          )
        ) : (
          <>
            <x.div
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap="sm"
            >
              <Checkbox
                checked={read}
                onChange={() => {
                  if (read) return;
                  setRead(true);
                }}
              >
                <x.div textAlign="left">
                  {t`I acknowledge that I have read, understand, and agree to the documents above.`}
                </x.div>
              </Checkbox>
            </x.div>
            <x.p mb="lg">
              <Button
                style={{ marginTop: '20px' }}
                onClick={async () => {
                  if (policiesForSignature) {
                    const policyIds = policiesForSignature
                      .map(policy => policy?.id)
                      .filter(Boolean);

                    await acknowledge({
                      variables: {
                        policyIds: policyIds,
                        learnerTrainingUuid: learnerTrainingId,
                        signature: ''
                      }
                    });
                  }
                  accept();
                }}
              >
                {t`Next`}
              </Button>
            </x.p>
          </>
        )}
        <Overlay open={open} onClick={() => setOpen(false)}>
          <Modal
            horizontal="center"
            open
            vertical="center"
            width="90%"
            height="90%"
          >
            <TitleForPopup>
              <span>{t`Please read this document and click here when you are done`}</span>
              <Button
                style={{ backgroundColor: '#9738FF', color: '#FFF' }}
                onClick={() => setOpen(false)}
              >{t`Close`}</Button>
            </TitleForPopup>
            <iframe
              src={urlForIframe}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          </Modal>
        </Overlay>
      </x.div>
    </>
  );
};
